/** Students {@link DataTable} for the management page. */
import * as React from 'react';
import { User as UserIcon } from 'lucide-react';
import { DataTable } from '@/components/common/data-table';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { Button } from '@/components/forms/Button';
import { StudentRow } from './StudentRow';
import { StudentCard } from './StudentCard';
import { StudentFiltersContent } from './StudentFilters';
import { StudentDetails } from './renderStudentDetails';
import { ArchiveStudentModal } from './ArchiveStudentModal';
import { ReactivateStudentModal } from './ReactivateStudentModal';
import { useListStudentsRemoteScoped, useGetStudentRemoteFullState } from '@/hooks/useUsers';
import { useDataTable } from '@/hooks/useDataTable';
import { useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { cn } from '@/lib/utils';
import { getRoleLabelPlural } from '@/utils/role-label-utils';
import { type StudentRemoteTabView as TabView } from '@/components/students/student-configs';
import {
  DEFAULT_STUDENT_FILTERS,
  ALL_STUDENT_FILTER_GROUP_IDS,
  STUDENT_FILTER_GROUP_LABELS,
  type StudentFilters,
  type FilterGroupId,
} from './StudentFilters';
import {
  buildStudentArchiveTarget,
  buildStudentRemoteListFilters,
  hasStudentRemoteActiveFilters,
  resolveStudentExpandedUserId,
  type StudentArchiveTarget,
  type StudentDataTablePageActions,
} from './student-table-utils';
import type { StudentRecord, OnboardingFilters } from '@/types/remote/student.types';
import type { ViewMode } from '@/types/data-table.types';
import type { ElementWidth } from '@/types/interface-elements.types';

/** List filters kept when exporting remote students to Excel. */
type StudentExportFilters = Omit<OnboardingFilters, 'companyId' | 'page' | 'pageSize'>;

/** Props for {@link StudentsDataTable}. */
interface Props {
  companyId: string;
  viewMode?: ViewMode;
  onViewModeChange?: (m: ViewMode) => void;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditStudent?: (id: string, record: StudentRecord) => void;
  onViewSessions?: (record: StudentRecord) => void;
  onViewReports?: (record: StudentRecord) => void;
  onViewParent?: (parentUserId: string) => void;
  enableFilters?: boolean;
  activeTab: TabView;
  pageActions?: StudentDataTablePageActions;
  onEditSection?: (stepKey: string, record: StudentRecord) => void;
  initialFilters?: Partial<StudentFilters>;
  showExport?: boolean;
  /** Same filters as the open list query (without page/pageSize). */
  onExportExcel?: (filters: StudentExportFilters) => void;
  showCreateButton?: boolean;
  onCreateStudent?: () => void;
  isSection?: boolean;
  layoutWidth?: ElementWidth;
  toolbarLeft?: React.ReactNode;
}

/** Students {@link DataTable} for the management page. */
export const StudentsDataTable: React.FC<Props> = ({
  companyId,
  viewMode: externalViewMode,
  onViewModeChange,
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditStudent,
  onViewSessions,
  onViewReports,
  onViewParent,
  enableFilters = true,
  activeTab,
  pageActions,
  onEditSection,
  initialFilters,
  showExport,
  onExportExcel,
  showCreateButton,
  onCreateStudent,
  isSection,
  layoutWidth,
  toolbarLeft,
}) => {
  const dt = useDataTable<StudentFilters, FilterGroupId>({
    tableName: 'students',
    defaultFilters: DEFAULT_STUDENT_FILTERS,
    allFilterGroupIds: ALL_STUDENT_FILTER_GROUP_IDS,
    externalViewMode,
    onExternalViewModeChange: onViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
    isSection,
    layoutWidth,
  });

  // Apply initialFilters when they change — compare with previous value so initial mount
  // (and React 18 StrictMode's double-invoke) are treated as "no change" and don't reset the page.
  const prevLinkedUserIdRef = React.useRef(initialFilters?.parentId);
  React.useEffect(() => {
    const prev = prevLinkedUserIdRef.current;
    prevLinkedUserIdRef.current = initialFilters?.parentId;
    if (prev === initialFilters?.parentId) {
      // Initial mount or StrictMode re-run: apply without resetting page
      if (initialFilters) dt.primeFilters((f) => ({ ...f, ...initialFilters }));
      return;
    }
    if (initialFilters) {
      dt.setFilters((f) => ({ ...f, ...initialFilters }));
      dt.setCurrentPage(1);
    }
  }, [initialFilters?.parentId]);

  // Reset to page 1 and clear tab-specific filters when tab changes.
  const prevActiveTabRef = React.useRef(activeTab);
  React.useEffect(() => {
    const prev = prevActiveTabRef.current;
    prevActiveTabRef.current = activeTab;
    if (prev === activeTab) return;
    dt.setCurrentPage(1);
    dt.setFilters((f) => ({
      ...f,
      studentSituation: undefined,
      stepKeys: undefined,
      userStatus: undefined,
    }));
  }, [activeTab]);

  const apiFilters = React.useMemo(
    () => ({
      ...buildStudentRemoteListFilters(dt.filters, activeTab),
      page: dt.currentPage,
      pageSize: dt.itemsPerPage,
    }),
    [dt.filters, activeTab, dt.currentPage, dt.itemsPerPage],
  );

  const exportFilters = React.useMemo(
    (): StudentExportFilters => buildStudentRemoteListFilters(dt.filters, activeTab),
    [dt.filters, activeTab],
  );

  const { data: apiResponse, isLoading } = useListStudentsRemoteScoped(apiFilters);

  const data = apiResponse?.data ?? [];
  const totalItems = apiResponse?.total ?? 0;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = Math.min(startIndex + dt.itemsPerPage, totalItems);

  const expandedUserId = resolveStudentExpandedUserId(expandedRowId, data);

  const {
    data: expandedFullState,
    isLoading: isLoadingExpandedStudent,
    isError: isExpandedStudentError,
    error: expandedStudentError,
  } = useGetStudentRemoteFullState(expandedUserId ?? '', companyId, {
    enabled: !!expandedUserId && !!expandedRowId,
  });
  useUnitGetNotFound({
    isError: isExpandedStudentError,
    error: expandedStudentError,
    catalogKey: 'studentNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  const expandedRecord = expandedFullState?.record ?? null;

  const [archiveTarget, setArchiveTarget] = React.useState<StudentArchiveTarget | null>(null);
  const [reactivateUserId, setReactivateUserId] = React.useState<string | null>(null);

  function handleArchive(userId: string): void {
    const record = data.find((row) => row.userId === userId);
    setArchiveTarget(buildStudentArchiveTarget(record, userId));
  }

  function handleReactivate(userId: string): void {
    setReactivateUserId(userId);
  }

  function handleCloseArchive(): void {
    setArchiveTarget(null);
  }

  function handleCloseReactivate(): void {
    setReactivateUserId(null);
  }

  const hasActiveFilters = React.useMemo(
    () => hasStudentRemoteActiveFilters(dt.filters),
    [dt.filters],
  );

  const customFilterRender = React.useCallback(
    () => (
      <StudentFiltersContent
        filters={dt.filters}
        visibleFilterIds={dt.visibleFilterIds as FilterGroupId[]}
        onFilterChange={(patch) => {
          dt.setFilters((prev) => ({ ...prev, ...patch }));
          dt.setCurrentPage(1);
        }}
        activeTab={activeTab}
      />
    ),
    [dt.filters, dt.visibleFilterIds, dt.setFilters, dt.setCurrentPage, activeTab],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: customFilterRender,
      },
    ],
    [customFilterRender],
  );

  return (
    <DataTable
      data={data}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="élèves"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={8}
      toolbarActions={
        enableFilters || showExport || showCreateButton ? (
          <>
            {enableFilters && (
              <Button
                preset="toolbar-filter"
                className={cn(dt.showFilters && 'section-action-button-active')}
                showBadge={hasActiveFilters}
                onClick={() => {
                  if (!dt.showFilters) dt.setShowConfigure(false);
                  dt.setShowFilters((prev) => !prev);
                }}
              />
            )}
            {showExport && onExportExcel && (
              <Button
                preset="toolbar-export"
                tooltipText="Exporter la liste des élèves en Excel"
                onClick={() => onExportExcel(exportFilters)}
              />
            )}
            {showCreateButton && onCreateStudent && (
              <Button
                preset="toolbar-create"
                tooltipText="Créer un élève"
                onClick={() => {
                  onCloseDetails?.();
                  onCreateStudent();
                }}
              />
            )}
          </>
        ) : undefined
      }
      filtersPanel={
        enableFilters ? (
          <DataTableFilters
            show={dt.showFilters}
            hasActiveFilters={hasActiveFilters}
            filterGroups={filterGroups}
            onClearFilters={() => {
              dt.clearFilters();
              dt.setCurrentPage(1);
            }}
            resultCount={totalItems}
            resultLabel="élève(s)"
            onToggleConfigure={
              dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
            }
            showConfigure={dt.showConfigure}
            configureContent={
              <div className="flex flex-wrap gap-3">
                {ALL_STUDENT_FILTER_GROUP_IDS.filter((id) => {
                  if (id === 'filter-registration-step' && activeTab !== 'pre_registered')
                    return false;
                  return dt.allowedFilterIds.includes(id);
                }).map((id) => {
                  const isOn = dt.filterVisibility[id] ?? true;
                  return (
                    <Button
                      key={id}
                      variant="custom"
                      size="sm"
                      onClick={() => dt.handleToggleFilterVisibility(id)}
                      disabled={dt.isSavingFilterVisibility}
                      className={cn(
                        dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                        isOn ? 'filter-option-primary--active' : 'filter-option-primary',
                      )}
                    >
                      {STUDENT_FILTER_GROUP_LABELS[id]}
                    </Button>
                  );
                })}
              </div>
            }
          />
        ) : undefined
      }
      renderTableHead={() => (
        <tr>
          <th>Nom</th>
          <th>Contact</th>
          <th>Progression</th>
          <th>Étapes</th>
          <th>Inscription</th>
          <th>Statut</th>
          <th className="w-20 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(record) => (
        <StudentRow
          record={record}
          onView={onToggleRow}
          onEdit={onEditStudent}
          onViewSessions={onViewSessions}
          onViewReports={onViewReports}
          onViewParent={onViewParent}
          pageActions={pageActions}
          activeTab={activeTab}
          onArchive={handleArchive}
          onReactivate={handleReactivate}
        />
      )}
      renderCard={(record) => (
        <StudentCard
          record={record}
          onView={onToggleRow}
          onEdit={onEditStudent}
          onViewSessions={onViewSessions}
          onViewReports={onViewReports}
          onViewParent={onViewParent}
          pageActions={pageActions}
          activeTab={activeTab}
          onArchive={handleArchive}
          onReactivate={handleReactivate}
        />
      )}
      expandedRowId={dt.expandedRowId}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      expandedItem={expandedRecord}
      isExpandedDetailLoading={isLoadingExpandedStudent}
      renderExpandedRow={(record) => (
        <StudentDetails
          record={record}
          onEditSection={onEditSection ? (stepKey) => onEditSection(stepKey, record) : undefined}
        />
      )}
      renderDetailsContent={(record) => (
        <StudentDetails
          record={record}
          onEditSection={onEditSection ? (stepKey) => onEditSection(stepKey, record) : undefined}
        />
      )}
      onCloseDetails={onCloseDetails}
      emptyIcon={UserIcon}
      emptyText="Aucun élève trouvé"
      isSection={isSection}
      modals={
        <>
          <ArchiveStudentModal
            open={!!archiveTarget}
            userId={archiveTarget?.userId ?? ''}
            companyId={companyId}
            studentName={archiveTarget?.studentName}
            isTutoringInProgress={archiveTarget?.isTutoringInProgress}
            onClose={handleCloseArchive}
          />
          <ReactivateStudentModal
            open={!!reactivateUserId}
            userId={reactivateUserId ?? ''}
            companyId={companyId}
            onClose={handleCloseReactivate}
            onViewParent={onViewParent}
          />
        </>
      }
      title={getRoleLabelPlural('student')}
      sidebarMenuId="menu-students"
      toolbarLeft={toolbarLeft}
    />
  );
};
